/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import SellerPackageCartItemRow from "pages/Front/Booking/Seller/components/SellerPackageCartItemRow";
import {useTranslation} from "react-i18next";
import {Reservation} from "types/entities/Reservation";
import clsx from "clsx";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import {
    BanknotesIcon,
    CircleStackIcon,
    Cog6ToothIcon,
    CurrencyDollarIcon,
    PercentBadgeIcon,
    PlusIcon,
    TagIcon,
} from "@heroicons/react/24/outline";
import {closeModal, openModal} from "helpers/actions";
import {ModalSize} from "pages/Front/components/Popup";
import {useDispatch} from "useDispatch";
import BookingSellerUpsellsModal
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerUpsellsModal";
import BookingAction from "store/booking/BookingAction";
import TableHeadColumn from "pages/Front/components/Table/TableHeadColumn";
import TableHeadTitle from "pages/Front/components/Table/TableHeadTitle";
import Table from "pages/Front/components/Table/Table";
import TableHead from "pages/Front/components/Table/TableHead";
import TableBody from "pages/Front/components/Table/TableBody";
import TableRow from "pages/Front/components/Table/TableRow";

interface BookingSellerPackagesCartItemsTableProps {
    packagesCartItems: PackagesCartItem[];
    reservation: Reservation;
    title: string;
    isAccessTicket?: boolean;
}

const BookingSellerPackagesCartItemsTable = (props: BookingSellerPackagesCartItemsTableProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const onClickAddUpsell = () => {
        dispatch(
            openModal(<BookingSellerUpsellsModal reservation={props.reservation} />, {
                modalSize: ModalSize.Scrollable,
                closeModal: () => {
                    dispatch(BookingAction.clearUpsells());
                    dispatch(closeModal());
                },
            })
        );
    };

    return (
        <BookingCard className={clsx("BookingSellerPackagesCartItemsTableContainer !px-0 !py-0")}>
            <h2
                className={clsx(
                    "font-bold text-xl text-black px-3 xl:px-3 py-2.5 flex items-center ",
                    props.isAccessTicket !== true ? "xl:py-2.5 justify-between" : "xl:py-3 justify-center sm:justify-start"
                )}
            >
                <div className={"inline-flex items-center gap-x-2"}>
                    <span>{props.title}</span>
                </div>
                {props.isAccessTicket !== true && (
                    <button
                        className={"bg-blue-400 hover:bg-blue-500 px-2 py-1 text-white rounded text-base flex flex-row items-center"}
                        onClick={onClickAddUpsell}
                    >
                        <PlusIcon className={"w-4 h-4 mr-1.5"} />
                        <span>{t("Add upsell")}</span>
                    </button>
                )}
            </h2>
            <Table className={"BookingSellerPackagesCartItemsTable border-t"} responsive={true}>
                <colgroup className={"hidden lg:table-column-group"}>
                    <col className="min-w-[300px] w-auto" />
                    <col className="w-[180px]" />
                    <col className="w-[120px]" />
                    <col className="w-[160px]" />
                    <col className="w-[160px]" />
                    <col className="w-[160px]" />
                    <col className="w-[140px]" />
                </colgroup>
                <TableHead>
                    <TableHeadColumn>
                        <TableHeadTitle>
                            <TagIcon className={"w-4 h-4 mr-1.5"} />
                            <span>{t("Item")}</span>
                        </TableHeadTitle>
                    </TableHeadColumn>
                    <TableHeadColumn>
                        <TableHeadTitle className={"justify-start"}>
                            <CircleStackIcon className={"w-4 h-4 mr-1.5"} />
                            <span>{t("Quantity")}</span>
                        </TableHeadTitle>
                    </TableHeadColumn>
                    <TableHeadColumn>
                        <TableHeadTitle className={"justify-start"}>
                            {props.isAccessTicket !== true ? (
                                <>
                                    <Cog6ToothIcon className={"w-4 h-4 mr-1.5"} />
                                    <span>{t("Actions")}</span>
                                </>
                            ) : (
                                <></>
                            )}
                        </TableHeadTitle>
                    </TableHeadColumn>
                    <TableHeadColumn>
                        <TableHeadTitle className={"justify-start"}>
                            <CurrencyDollarIcon className={"w-4 h-4 mr-1.5"} />
                            <span>{t("Unit price")}</span>
                        </TableHeadTitle>
                    </TableHeadColumn>
                    <TableHeadColumn>
                        <TableHeadTitle className={"justify-start space-x-1"}>
                            <CurrencyDollarIcon className={"w-4 h-4 mr-0.5"} />
                            <div className={"flex flex-col leading-[1] justify-center"}>
                                <span>{t("Unit price")}</span><span className={"text-xs"}>{t("with discount")}</span>
                            </div>
                        </TableHeadTitle>
                    </TableHeadColumn>
                    <TableHeadColumn>
                        <TableHeadTitle className={"justify-start"}>
                            <PercentBadgeIcon className={"w-4 h-4 mr-1.5"} />
                            <span>{t("Discount")}</span>
                        </TableHeadTitle>
                    </TableHeadColumn>
                    <TableHeadColumn>
                        <TableHeadTitle className={"justify-end"}>
                            <BanknotesIcon className={"w-4 h-4 mr-1.5"} />
                            <span>{t("Price")}</span>
                        </TableHeadTitle>
                    </TableHeadColumn>
                </TableHead>
                <TableBody>
                    {props.packagesCartItems.length > 0 ? (
                        props.packagesCartItems
                            .sort((a, b) => (a.packageItem!.article_id > b.packageItem!.article_id ? 1 : -1))
                            .map((packagesCartItem, index) => (
                                <SellerPackageCartItemRow
                                    packagesCartItem={packagesCartItem}
                                    isAccessTicket={props.isAccessTicket}
                                    reservation={props.reservation}
                                    key={index}
                                />
                            ))
                    ) : (
                        <TableRow>
                            <td className={"px-3 py-2 text-center"} colSpan={6}>
                                {t("No items")}
                            </td>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </BookingCard>
    );
};

export default BookingSellerPackagesCartItemsTable;
